import React, { useState, useEffect } from 'react';
import { MessageCircle, TrendingUp, TrendingDown, ThumbsUp, Users, Loader, RefreshCw, ExternalLink, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'https://financial-analyst-backend-production-7175.up.railway.app/api';

const SocialSentiment = ({ symbol }) => {
  const [socialData, setSocialData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeframe, setTimeframe] = useState('week');

  useEffect(() => {
    if (symbol) {
      fetchSocialData();
    }
  }, [symbol, timeframe]);

  const fetchSocialData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/reddit/sentiment/${symbol}?timeframe=${timeframe}`);
      const result = await response.json();
      
      if (result.success && result.data) {
        // Transform Reddit data to match existing UI structure
        const transformed = transformRedditData(result.data);
        setSocialData(transformed);
      } else {
        throw new Error(result.error || 'Failed to fetch sentiment');
      }
    } catch (err) {
      console.error('Failed to fetch social sentiment:', err);
      setError('Unable to fetch Reddit sentiment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Transform Reddit API response to match existing UI data structure
  const transformRedditData = (data) => {
    const { analysis, engagement, topPosts, subredditBreakdown } = data;
    
    // Calculate trend based on sentiment
    let trend = 'neutral';
    if (analysis?.sentimentScore > 20) trend = 'up';
    else if (analysis?.sentimentScore < -20) trend = 'down';
    
    // Map sentiment label
    let sentimentLabel = 'Neutral';
    if (analysis?.overallSentiment === 'bullish') sentimentLabel = 'Bullish';
    else if (analysis?.overallSentiment === 'bearish') sentimentLabel = 'Bearish';
    else if (analysis?.overallSentiment === 'mixed') sentimentLabel = 'Mixed';
    
    // Normalize sentiment score to -1 to 1 range
    const normalizedScore = (analysis?.sentimentScore || 0) / 100;
    
    return {
      mentions24h: engagement?.recentPostCount || engagement?.totalPosts || 0,
      mentions7d: engagement?.totalPosts || 0,
      sentimentScore: normalizedScore,
      sentimentLabel,
      trend,
      totalUpvotes: engagement?.totalScore || 0,
      totalComments: engagement?.totalComments || 0,
      trendingScore: engagement?.trendingScore || 0,
      confidence: analysis?.confidence || 0,
      bullishPercent: analysis?.bullishPercent || 0,
      bearishPercent: analysis?.bearishPercent || 0,
      neutralPercent: analysis?.neutralPercent || 0,
      keyThemes: analysis?.keyThemes || [],
      bullishPoints: analysis?.topBullishPoints || [],
      bearishPoints: analysis?.topBearishPoints || [],
      summary: analysis?.summary || '',
      subreddits: subredditBreakdown || [],
      posts: (topPosts || []).map(post => ({
        id: post.id,
        title: post.title,
        source: `r/${post.subreddit}`,
        timestamp: post.created,
        upvotes: post.score || 0,
        comments: post.comments || 0,
        url: post.url,
        sentiment: getSentimentFromScore(post.score),
        score: post.score > 100 ? 0.5 : post.score > 0 ? 0.2 : -0.2
      })),
      isRealData: true,
      fetchedAt: data.fetchedAt
    };
  };

  const getSentimentFromScore = (score) => {
    if (score > 500) return 'Bullish';
    if (score > 100) return 'Somewhat-Bullish';
    if (score < 0) return 'Bearish';
    return 'Neutral';
  };

  const getSentimentColor = (score) => {
    if (typeof score === 'string') {
      if (score === 'Bullish' || score === 'Somewhat-Bullish') return '#10b981';
      if (score === 'Bearish') return '#ef4444';
      return '#f59e0b';
    }
    if (score > 0.3) return '#10b981';
    if (score < -0.3) return '#ef4444';
    return '#f59e0b';
  };

  const getSentimentIcon = (label) => {
    if (label === 'Bullish' || label === 'Somewhat-Bullish') return <TrendingUp size={16} />;
    if (label === 'Bearish') return <TrendingDown size={16} />;
    return <TrendingUp size={16} style={{ transform: 'rotate(90deg)' }} />;
  };

  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffMs = now - time;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    
    if (diffHours < 1) return 'Just now';
    if (diffHours === 1) return '1 hour ago';
    if (diffHours < 24) return `${diffHours} hours ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) return '1 day ago';
    return `${diffDays} days ago`;
  };

  if (loading) {
    return (
      <div style={{ 
        background: 'rgba(30, 41, 59, 0.5)', 
        border: '1px solid #334155', 
        borderRadius: '0.75rem', 
        padding: '2rem',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '0.75rem'
      }}>
        <Loader size={32} color="#ff4500" className="spin" />
        <span style={{ color: '#94a3b8', fontSize: '0.875rem' }}>Analyzing Reddit discussions...</span>
      </div>
    );
  }

  if (error || !socialData) {
    return (
      <div style={{ 
        background: 'rgba(239, 68, 68, 0.1)', 
        border: '1px solid rgba(239, 68, 68, 0.3)', 
        borderRadius: '0.75rem', 
        padding: '1.5rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <AlertCircle size={18} color="#f87171" />
          <p style={{ color: '#f87171', margin: 0, fontWeight: '600' }}>Reddit Sentiment Unavailable</p>
        </div>
        <p style={{ color: '#fca5a5', margin: '0 0 1rem 0', fontSize: '0.875rem' }}>{error || 'No social data available'}</p>
        <button
          onClick={fetchSocialData}
          style={{
            padding: '0.5rem 1rem',
            background: '#ef4444',
            border: 'none',
            borderRadius: '0.5rem',
            color: 'white',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontSize: '0.875rem'
          }}
        >
          <RefreshCw size={14} />
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '1.5rem',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <h3 style={{ 
          fontSize: '1.25rem', 
          fontWeight: 'bold', 
          margin: 0, 
          display: 'flex', 
          alignItems: 'center', 
          gap: '0.5rem' 
        }}>
          <MessageCircle size={20} color="#ff4500" />
          Social Sentiment
          <span style={{ 
            fontSize: '0.625rem', 
            padding: '0.125rem 0.5rem', 
            background: 'rgba(255, 69, 0, 0.2)', 
            color: '#ff6b35',
            borderRadius: '0.25rem',
            fontWeight: '500',
            marginLeft: '0.25rem'
          }}>
            REDDIT
          </span>
        </h3>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {/* Timeframe selector */}
          <select
            value={timeframe}
            onChange={(e) => setTimeframe(e.target.value)}
            style={{
              padding: '0.375rem 0.75rem',
              background: '#1e293b',
              border: '1px solid #334155',
              borderRadius: '0.375rem',
              color: '#e2e8f0',
              fontSize: '0.75rem',
              cursor: 'pointer'
            }}
          >
            <option value="day">24 Hours</option>
            <option value="week">Past Week</option>
            <option value="month">Past Month</option>
          </select>
          
          <button
            onClick={fetchSocialData}
            disabled={loading}
            style={{
              padding: '0.5rem 1rem',
              background: '#059669',
              border: 'none',
              borderRadius: '0.5rem',
              color: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.875rem',
              fontWeight: '600',
              opacity: loading ? 0.7 : 1
            }}
          >
            <RefreshCw size={14} className={loading ? 'spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* Sentiment Overview - Keep exact same layout */}
      <div style={{
        background: 'rgba(30, 41, 59, 0.5)',
        border: '1px solid #334155',
        borderRadius: '0.75rem',
        padding: '1.5rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
          {/* Mentions */}
          <div>
            <div style={{ fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.5rem' }}>
              <Users size={16} style={{ display: 'inline', marginRight: '0.5rem' }} />
              Mentions ({timeframe === 'day' ? '24h' : timeframe === 'week' ? '7d' : '30d'})
            </div>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#8b5cf6' }}>
              {socialData.mentions7d.toLocaleString()}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem' }}>
              {socialData.totalUpvotes.toLocaleString()} total upvotes
            </div>
          </div>

          {/* Overall Sentiment */}
          <div>
            <div style={{ fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.5rem' }}>
              Overall Sentiment
            </div>
            <div style={{ 
              fontSize: '2rem', 
              fontWeight: 'bold',
              color: getSentimentColor(socialData.sentimentScore),
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              {getSentimentIcon(socialData.sentimentLabel)}
              {socialData.sentimentLabel}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem' }}>
              Score: {socialData.sentimentScore.toFixed(2)} • {socialData.confidence}% confidence
            </div>
          </div>

          {/* Trend */}
          <div>
            <div style={{ fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.5rem' }}>
              Trend
            </div>
            <div style={{ 
              fontSize: '1.5rem', 
              fontWeight: 'bold',
              color: socialData.trend === 'up' ? '#10b981' : socialData.trend === 'down' ? '#ef4444' : '#f59e0b',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              {socialData.trend === 'up' ? <TrendingUp size={24} /> : 
               socialData.trend === 'down' ? <TrendingDown size={24} /> : 
               <span style={{ fontSize: '1.25rem' }}>→</span>}
              {socialData.trend.toUpperCase()}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem' }}>
              vs. previous period
            </div>
          </div>
        </div>

        {/* Sentiment Breakdown Bar */}
        <div style={{ marginTop: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <span style={{ fontSize: '0.75rem', color: '#10b981' }}>🐂 Bullish {socialData.bullishPercent}%</span>
            <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>Neutral {socialData.neutralPercent}%</span>
            <span style={{ fontSize: '0.75rem', color: '#ef4444' }}>Bearish {socialData.bearishPercent}% 🐻</span>
          </div>
          <div style={{ 
            display: 'flex', 
            height: '8px', 
            borderRadius: '4px', 
            overflow: 'hidden',
            background: '#1e293b'
          }}>
            <div style={{ width: `${socialData.bullishPercent}%`, background: '#10b981', transition: 'width 0.3s' }} />
            <div style={{ width: `${socialData.neutralPercent}%`, background: '#6b7280', transition: 'width 0.3s' }} />
            <div style={{ width: `${socialData.bearishPercent}%`, background: '#ef4444', transition: 'width 0.3s' }} />
          </div>
        </div>
      </div>

      {/* Key Insights (if available) */}
      {(socialData.keyThemes?.length > 0 || socialData.bullishPoints?.length > 0 || socialData.bearishPoints?.length > 0) && (
        <div style={{
          background: 'rgba(30, 41, 59, 0.5)',
          border: '1px solid #334155',
          borderRadius: '0.75rem',
          padding: '1.5rem',
          marginBottom: '1.5rem'
        }}>
          <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', color: '#cbd5e1' }}>
            Key Insights
          </h4>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
            {/* Key Themes */}
            {socialData.keyThemes?.length > 0 && (
              <div>
                <div style={{ fontSize: '0.75rem', color: '#8b5cf6', marginBottom: '0.5rem', fontWeight: '600' }}>
                  🎯 Key Themes
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>
                  {socialData.keyThemes.map((theme, i) => (
                    <span key={i} style={{
                      fontSize: '0.7rem',
                      padding: '0.25rem 0.5rem',
                      background: 'rgba(139, 92, 246, 0.2)',
                      color: '#c4b5fd',
                      borderRadius: '0.25rem'
                    }}>
                      {theme}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Bullish Points */}
            {socialData.bullishPoints?.length > 0 && (
              <div>
                <div style={{ fontSize: '0.75rem', color: '#10b981', marginBottom: '0.5rem', fontWeight: '600' }}>
                  ✅ Bullish Sentiment
                </div>
                {socialData.bullishPoints.slice(0, 2).map((point, i) => (
                  <div key={i} style={{ fontSize: '0.75rem', color: '#a7f3d0', marginBottom: '0.25rem' }}>
                    • {point}
                  </div>
                ))}
              </div>
            )}

            {/* Bearish Points */}
            {socialData.bearishPoints?.length > 0 && (
              <div>
                <div style={{ fontSize: '0.75rem', color: '#ef4444', marginBottom: '0.5rem', fontWeight: '600' }}>
                  ⚠️ Bearish Concerns
                </div>
                {socialData.bearishPoints.slice(0, 2).map((point, i) => (
                  <div key={i} style={{ fontSize: '0.75rem', color: '#fca5a5', marginBottom: '0.25rem' }}>
                    • {point}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Summary */}
          {socialData.summary && (
            <div style={{
              marginTop: '1rem',
              padding: '0.75rem',
              background: 'rgba(96, 165, 250, 0.1)',
              border: '1px solid rgba(96, 165, 250, 0.3)',
              borderRadius: '0.5rem',
              fontSize: '0.8rem',
              color: '#93c5fd',
              lineHeight: 1.5
            }}>
              📊 {socialData.summary}
            </div>
          )}
        </div>
      )}

      {/* Subreddit Breakdown */}
      {socialData.subreddits?.length > 0 && (
        <div style={{
          background: 'rgba(30, 41, 59, 0.5)',
          border: '1px solid #334155',
          borderRadius: '0.75rem',
          padding: '1.5rem',
          marginBottom: '1.5rem'
        }}>
          <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', color: '#cbd5e1' }}>
            Activity by Subreddit
          </h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.5rem' }}>
            {socialData.subreddits.slice(0, 6).map((sub, i) => (
              <div key={i} style={{
                background: 'rgba(51, 65, 85, 0.5)',
                border: '1px solid #475569',
                borderRadius: '0.375rem',
                padding: '0.5rem 0.75rem'
              }}>
                <div style={{ fontSize: '0.8rem', fontWeight: '600', color: '#ff4500' }}>
                  r/{sub.subreddit}
                </div>
                <div style={{ fontSize: '0.7rem', color: '#64748b' }}>
                  {sub.count} posts • {sub.totalScore.toLocaleString()} upvotes
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top Posts - Keep exact same layout */}
      <div style={{
        background: 'rgba(30, 41, 59, 0.5)',
        border: '1px solid #334155',
        borderRadius: '0.75rem',
        padding: '1.5rem'
      }}>
        <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', color: '#cbd5e1' }}>
          Recent Community Discussions
        </h4>
        
        {socialData.posts.length === 0 ? (
          <p style={{ color: '#94a3b8', textAlign: 'center', padding: '2rem' }}>
            No recent posts available for {symbol}
          </p>
        ) : (
          <div style={{ display: 'grid', gap: '0.75rem' }}>
            {socialData.posts.slice(0, 5).map((post, idx) => (
              <a
                key={idx}
                href={post.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  background: 'rgba(51, 65, 85, 0.5)',
                  border: '1px solid #475569',
                  borderRadius: '0.5rem',
                  padding: '1rem',
                  textDecoration: 'none',
                  display: 'block',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => e.currentTarget.style.borderColor = '#ff4500'}
                onMouseOut={(e) => e.currentTarget.style.borderColor = '#475569'}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: '1rem', marginBottom: '0.75rem' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#cbd5e1', marginBottom: '0.25rem' }}>
                      {post.title}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                      {post.source} • {formatTimestamp(post.timestamp)}
                    </div>
                  </div>
                  <div style={{
                    padding: '0.25rem 0.75rem',
                    background: `${getSentimentColor(post.sentiment)}20`,
                    border: `1px solid ${getSentimentColor(post.sentiment)}40`,
                    borderRadius: '0.375rem',
                    fontSize: '0.75rem',
                    color: getSentimentColor(post.sentiment),
                    fontWeight: '600',
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem'
                  }}>
                    {getSentimentIcon(post.sentiment)}
                    {post.sentiment}
                  </div>
                </div>
                <div style={{ 
                  fontSize: '0.75rem', 
                  color: '#94a3b8',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem'
                }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <ThumbsUp size={12} />
                    {post.upvotes.toLocaleString()} upvotes
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <MessageCircle size={12} />
                    {post.comments} comments
                  </span>
                  <ExternalLink size={12} style={{ marginLeft: 'auto', color: '#64748b' }} />
                </div>
              </a>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginTop: '1rem',
        fontSize: '0.7rem',
        color: '#475569'
      }}>
        <span>Data from Reddit • {socialData.posts.length} posts analyzed</span>
        {socialData.fetchedAt && (
          <span>Updated {formatTimestamp(socialData.fetchedAt)}</span>
        )}
      </div>
    </div>
  );
};

export default SocialSentiment;